import { useState } from 'react';
import { Copy, Plus } from 'lucide-react';
import { Button } from '@sara/ui/components/button';
import { cn } from '@sara/ui/lib/utils';
import { PlatformVariablesList, copyVariableToken } from '../PlatformVariablesList';
import { EditVariableDialog } from './EditVariableDialog';
import { VARIABLE_TYPE_LABELS } from './schemas';
import type { TemplateVariableValue } from './schemas';

interface TemplateVariablesFieldProps {
  value: TemplateVariableValue[];
  onChange: (next: TemplateVariableValue[]) => void;
  disabled?: boolean;
}

const MAX_VARIABLES = 20;

/** The single detail shown on the right of a row — only what's non-default. */
function rowDetail(v: TemplateVariableValue): string | null {
  if (v.allowedValues.length > 0) {
    return `${v.allowedValues.length} allowed value${v.allowedValues.length === 1 ? '' : 's'}`;
  }
  if (v.type !== 'TEXT') return VARIABLE_TYPE_LABELS[v.type].toLowerCase();
  if (v.defaultValue) return `defaults to “${v.defaultValue}”`;
  return null;
}

/**
 * The Variables section of the version form: the platform-provided variables
 * (read-only, always available to the prompt), then one calm row per declared
 * variable (required dot · name · {{token}} · one detail · Edit), an Add
 * button, and the add/edit dialog. Order is display + capture order. Fully
 * controlled — commits every change up through onChange so the parent form
 * owns the value.
 */
export function TemplateVariablesField({ value, onChange, disabled }: TemplateVariablesFieldProps) {
  const [editing, setEditing] = useState<{ index: number | null } | null>(null);

  const editingIndex = editing !== null ? editing.index : null;

  const handleSave = (variable: TemplateVariableValue) => {
    if (editingIndex !== null) {
      onChange(value.map((v, i) => (i === editingIndex ? variable : v)));
    } else {
      onChange([...value, variable]);
    }
    setEditing(null);
  };

  return (
    <div className="space-y-3">
      <PlatformVariablesList />

      <p className="text-[12px] font-medium text-foreground/80">Custom</p>
      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No custom variables yet. Add one if the agent&rsquo;s prompt uses{' '}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
            {'{{placeholders}}'}
          </code>{' '}
          beyond the platform&rsquo;s.
        </p>
      ) : (
        <ul className="divide-y divide-border/60 rounded-lg border">
          {value.map((v, index) => {
            const detail = rowDetail(v);
            return (
              <li key={v.key || index} className="flex items-center gap-3 px-4 py-3">
                <span
                  aria-hidden
                  title={v.required ? 'Required' : 'Optional'}
                  className={cn(
                    'h-1.5 w-1.5 shrink-0 rounded-full',
                    v.required ? 'bg-primary' : 'border border-muted-foreground/40'
                  )}
                />
                <span className="min-w-0 shrink-0 truncate text-sm font-medium">{v.name}</span>
                <button
                  type="button"
                  onClick={() => copyVariableToken(v.key)}
                  title="Copy token"
                  className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary transition-colors hover:bg-primary/20"
                >
                  {`{{${v.key}}}`}
                  <Copy className="h-3 w-3 opacity-60" />
                </button>
                {detail ? (
                  <span className="ml-auto truncate text-xs text-muted-foreground">{detail}</span>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={detail ? '' : 'ml-auto'}
                  onClick={() => setEditing({ index })}
                  disabled={disabled}
                >
                  Edit
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setEditing({ index: null })}
        disabled={disabled || value.length >= MAX_VARIABLES}
      >
        <Plus className="h-4 w-4" /> Add variable
      </Button>

      <EditVariableDialog
        open={editing !== null}
        initial={editingIndex !== null ? value[editingIndex] : null}
        takenKeys={value.filter((_, i) => i !== editingIndex).map((v) => v.key)}
        onSave={handleSave}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
    </div>
  );
}
